/**
 * AgaveDropdownColumnModule - Agave ERP Dropdown Columns for UniversalTransactionsTable
 *
 * This module provides the Agave-specific dropdown columns:
 * - GL Code, Project, SubJob, CostCode, CostType, Vendor, Location, Department,
 *   Employee, Subaccount, AccountingDate
 *
 * ARCHITECTURE:
 * - renderAgaveDropdown is the single source of truth for all dropdown rendering
 * - Two display configurations use it: TanStack table columns and sidesheet form
 * - A readOnly param controls enabled/disabled state and skips data fetching
 * - Every cell resolves the edit predicate, readOnly columns included, because
 *   it needs hooks and so cannot sit behind a branch; the interactive renderer
 *   then calls its own hooks once
 */

import { useMemo, useRef } from 'react';
import { Box, MenuItem, Select, Tooltip, Typography, type SelectChangeEvent } from '@mui/material';
import type { ColumnDef, CellContext } from '@tanstack/react-table';

import { HighnoteTransactionEvent, ERPPostingStatus } from 'API';
import {
  useSpectrumPostingVendorLock,
  SpectrumPostingVendorLock,
} from 'app/hooks/useSpectrumPostingVendorLock';
import { useAppSelector, useAppDispatch } from 'app/store';
import {
  selectTransactionById,
  selectTransactionsSlice,
  selectCodingTypeOverride,
} from 'app/pages/store/transactionsSlice';
import { setCodingTypeForTransaction } from 'app/pages/store/agaveSlice';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useAgaveClientUtils } from 'app/pages/overview/tabs/transactions/AgaveTransactions/utils';
import { useCanEditTransaction } from 'app/hooks/transactions/useCanEditTransaction';
import { AgaveStatusChip } from 'app/pages/overview/tabs/transactions/AgaveTransactions/AgaveStatusChip';
import {
  ReturnedStatusChip,
  shouldShowReturnedStatus,
} from 'app/pages/overview/tabs/transactions/ReturnedStatusChip';
import { useUserRole } from 'app/hooks/useUserRole';
import { AccountingDate } from 'app/pages/overview/tabs/transactions/AccountingDate/AccountingDate';
import AgaveGlCodeDropdown from 'app/pages/overview/tabs/transactions/AgaveTransactions/AgaveDropdowns/AgaveGlCodesDropdown';
import AgaveProjectsDropdown from 'app/pages/overview/tabs/transactions/AgaveTransactions/AgaveDropdowns/AgaveProjectsDropdown';
import AgaveSubJobsDropdown from 'app/pages/overview/tabs/transactions/AgaveTransactions/AgaveDropdowns/AgaveSubJobsDropdown';
import AgaveCostCodesDropdown from 'app/pages/overview/tabs/transactions/AgaveTransactions/AgaveDropdowns/AgaveCostCodesDropdown';
import AgaveCostTypesDropdown from 'app/pages/overview/tabs/transactions/AgaveTransactions/AgaveDropdowns/AgaveCostTypesDropdown';
import AgaveVendorsDropdown from 'app/pages/overview/tabs/transactions/AgaveTransactions/AgaveDropdowns/AgaveVendorsDropdown';
import AgaveLocationsDropdown from 'app/pages/overview/tabs/transactions/AgaveTransactions/AgaveDropdowns/AgaveLocationsDropdown';
import AgaveDepartmentsDropdown from 'app/pages/overview/tabs/transactions/AgaveTransactions/AgaveDropdowns/AgaveDepartmentsDropdown';
import AgaveSubaccountsDropdown from 'app/pages/overview/tabs/transactions/AgaveTransactions/AgaveDropdowns/AgaveSubaccountsDropdown';
import AgaveEmployeesDropdown from 'app/pages/overview/tabs/transactions/AgaveTransactions/AgaveDropdowns/AgaveEmployeesDropdown';
import ErpBillableItemDropdown from 'app/pages/overview/tabs/transactions/AgaveTransactions/AgaveDropdowns/ErpBillableItemDropdown';
import {
  AgaveEquipmentDropdown,
  AgaveEmCostCodeDropdown,
  AgaveEmCostTypeDropdown,
} from 'app/pages/overview/tabs/transactions/AgaveTransactions/AgaveDropdowns/AgaveEquipmentDropdowns';
import { formatDateOnly } from 'util/dateUtils';
import { CodingSidesheetGrid } from 'app/pages/overview/tabs/transactions/CodingSidesheetGrid';
import type { CodingColumnId } from 'app/util/columnVisibility';
import {
  noop,
  resolveDisplayCodingType,
  CODING_COLUMNS_BY_TYPE,
  type CodingType,
} from 'app/util/columnVisibility';
import type {
  CodingEntityRow,
  DropdownColumnModule,
  ModuleColumnProps,
  ToggleableColumn,
} from 'app/pages/overview/tabs/transactions/UniversalTransactionsTable';

// ---------------------------------------------------------------------------
// Type-safe handler contract derived from useAgaveClientUtils
// ---------------------------------------------------------------------------

type AgaveUtils = ReturnType<typeof useAgaveClientUtils>;
export type AgaveSidesheetHandlers = Pick<
  AgaveUtils,
  | 'handleChangeGlCodes'
  | 'handleChangeProjects'
  | 'handleChangeSubJob'
  | 'handleChangeCostCode'
  | 'handleChangeCostType'
  | 'handleChangeEquipment'
  | 'handleChangeEmCostCode'
  | 'handleChangeEmCostType'
  | 'handleChangeVendor'
  | 'handleChangeLocation'
  | 'handleChangeDepartment'
  | 'handleChangeSubaccount'
  | 'handleChangeEmployee'
  | 'handleChangeBillableItem'
  | 'handleChangeBillable'
  | 'handleChangeAccountingDate'
>;

const noopHandlers: AgaveSidesheetHandlers = {
  handleChangeGlCodes: noop,
  handleChangeProjects: noop,
  handleChangeSubJob: noop,
  handleChangeCostCode: noop,
  handleChangeCostType: noop,
  handleChangeEquipment: noop,
  handleChangeEmCostCode: noop,
  handleChangeEmCostType: noop,
  handleChangeVendor: noop,
  handleChangeLocation: noop,
  handleChangeDepartment: noop,
  handleChangeSubaccount: noop,
  handleChangeEmployee: noop,
  handleChangeBillableItem: noop,
  handleChangeBillable: noop,
  handleChangeAccountingDate: noop,
};

// ---------------------------------------------------------------------------
// Single source of truth: renders any Agave dropdown by type
// readOnly=true prevents data fetching (enabled=false) and uses plain text for dates
// ---------------------------------------------------------------------------

function renderAgaveDropdown(
  type: CodingColumnId,
  transaction: CodingEntityRow,
  disabled: boolean,
  handlers: AgaveSidesheetHandlers,
  readOnly = false,
  // SEE-944 — when true (paidol toggle on), the projects dropdown is narrowed
  // to projects mapped to the transaction's current location. Location itself
  // is driven by the selected employee (auto-filled when the employee maps to
  // exactly one location, otherwise the location dropdown narrows to that
  // employee's set so the user picks one). Resolved by callers because this
  // helper is a plain function and can't use hooks.
  filterProjectsByEmployeeLocation = false,
  // SEE-835 — when set (Spectrum direct + a Connections posting vendor), the
  // vendor dropdown is pre-filled with that posting vendor and locked. Resolved
  // by the caller (hook) and only passed on the interactive/unposted path.
  spectrumVendorLock?: SpectrumPostingVendorLock,
  // SEE-1390 (review) — whether this surface persists erpBillable. The
  // reimbursement sheets render this form but their payload builders don't
  // carry the flag, so the Billable checkbox must not render there (a flip
  // that saves nothing is worse than no control).
  supportsBillableFlip = true,
  // Table cells: the project-scoped lists (sub jobs, cost codes, cost types) are
  // loaded when their dropdown opens, not on mount. Every row used to fire its own
  // three searches on page load (25 rows: 40-80 calls of 1-3 s each on a slow stage),
  // which is what made the table feel stuck after any refresh. The row's label comes
  // from the coded entity on the row, so nothing shows differently. The side sheet
  // keeps loading eagerly: one row, and its lists cascade off each other.
  fetchOnlyOnOpen = false
): React.ReactElement | null {
  const enabled = !readOnly && !disabled;
  const txn = readOnly ? undefined : (transaction as HighnoteTransactionEvent);
  // SEE-944: scope project search to the selected location. Use the scalar
  // agaveLocationId as source of truth — the nested agaveLocation object isn't
  // always hydrated on the transaction (the list/detail query and some
  // post-update states populate only the scalar), so reading agaveLocation?.id
  // alone left locationId undefined and the project location filter silently
  // no-op'd (search returned every project).
  const selectedLocationId =
    transaction.agaveLocation?.id ?? (transaction as HighnoteTransactionEvent).agaveLocationId;
  const projectLocationId =
    filterProjectsByEmployeeLocation && selectedLocationId ? selectedLocationId : undefined;

  switch (type) {
    case 'glCode':
      return (
        <AgaveGlCodeDropdown
          disableHeader
          transaction={txn}
          selectedCode={transaction.agaveGlCode}
          onValueChange={(option) => handlers.handleChangeGlCodes(option!)}
          disabled={disabled}
          enabled={enabled}
        />
      );
    case 'project':
      return (
        <AgaveProjectsDropdown
          disableHeader
          transaction={txn}
          selectedProject={transaction.agaveProject}
          onValueChange={(option) => handlers.handleChangeProjects(option!)}
          disabled={disabled}
          enabled={enabled}
          locationId={projectLocationId}
        />
      );
    case 'subJob':
      return (
        <AgaveSubJobsDropdown
          disableHeader
          selectedSubJob={transaction.agaveSubJob}
          selectedProjectId={transaction.agaveProject?.id}
          onValueChange={(option) => handlers.handleChangeSubJob(option!)}
          disabled={disabled}
          enabled={enabled}
          fetchOnlyOnOpen={fetchOnlyOnOpen}
        />
      );
    case 'costCode':
      return (
        <AgaveCostCodesDropdown
          disableHeader
          selectedCostCode={transaction.agaveCostCode}
          selectedProject={transaction.agaveProject}
          selectedSubJob={transaction.agaveSubJob}
          onValueChange={(option) => handlers.handleChangeCostCode(option!)}
          disabled={disabled}
          enabled={enabled}
          fetchOnlyOnOpen={fetchOnlyOnOpen}
        />
      );
    case 'costType':
      return (
        <AgaveCostTypesDropdown
          disableHeader
          selectedCostType={transaction.agaveCostType}
          selectedProject={transaction.agaveProject}
          selectedCostCode={transaction.agaveCostCode}
          onValueChange={(option) => handlers.handleChangeCostType(option!)}
          disabled={disabled}
          enabled={enabled}
          fetchOnlyOnOpen={fetchOnlyOnOpen}
        />
      );
    case 'equipment':
      return (
        <AgaveEquipmentDropdown
          selectedEquipment={(transaction as HighnoteTransactionEvent).agaveEquipment}
          onValueChange={(option) => handlers.handleChangeEquipment(option)}
          disabled={disabled}
          enabled={enabled}
        />
      );
    case 'emCostCode':
      return (
        <AgaveEmCostCodeDropdown
          selectedEmCostCode={(transaction as HighnoteTransactionEvent).agaveEmCostCode}
          emGroup={(transaction as HighnoteTransactionEvent).agaveEquipment?.emGroup}
          emCo={(transaction as HighnoteTransactionEvent).agaveEquipment?.emCo}
          department={(transaction as HighnoteTransactionEvent).agaveEquipment?.department}
          onValueChange={(option) => handlers.handleChangeEmCostCode(option)}
          disabled={disabled}
          enabled={enabled}
        />
      );
    case 'emCostType':
      return (
        <AgaveEmCostTypeDropdown
          selectedEmCostType={(transaction as HighnoteTransactionEvent).agaveEmCostType}
          emGroup={(transaction as HighnoteTransactionEvent).agaveEquipment?.emGroup}
          emCo={(transaction as HighnoteTransactionEvent).agaveEquipment?.emCo}
          department={(transaction as HighnoteTransactionEvent).agaveEquipment?.department}
          selectedEmCostCodeId={(transaction as HighnoteTransactionEvent).agaveEmCostCode?.id}
          onValueChange={(option) => handlers.handleChangeEmCostType(option)}
          disabled={disabled}
          enabled={enabled}
        />
      );
    case 'vendor': {
      // SEE-835: Spectrum direct posts every transaction against the Connections
      // posting vendor and drops the per-transaction one — pre-fill + lock the
      // dropdown to that vendor so the UI matches what posts. Display-only: the
      // stored coding is untouched. Only the interactive (unposted) path receives
      // the lock; posted rows render read-only with their historical vendor.
      const vendorLock = spectrumVendorLock?.locked ? spectrumVendorLock : null;
      const vendorDropdown = (
        <AgaveVendorsDropdown
          disableHeader
          selectedVendor={vendorLock ? vendorLock.vendor : transaction.agaveVendor}
          onValueChange={(option) => handlers.handleChangeVendor(option)}
          disabled={disabled || !!vendorLock}
          enabled={enabled && !vendorLock}
        />
      );
      return vendorLock ? (
        <Tooltip title={vendorLock.tooltip}>
          <Box width="100%">{vendorDropdown}</Box>
        </Tooltip>
      ) : (
        vendorDropdown
      );
    }
    case 'location':
      return (
        <AgaveLocationsDropdown
          disableHeader
          selectedLocation={transaction.agaveLocation}
          selectedProject={transaction.agaveProject}
          // SEE-944 — only pass the selected employee when the paidol toggle
          // is on so the locations dropdown narrows to that employee's set.
          // When the toggle is off we preserve SEE-808's "show the full list"
          // behavior so paidols who haven't opted in see no change.
          selectedEmployee={filterProjectsByEmployeeLocation ? transaction.agaveEmployee : null}
          onValueChange={(option) => handlers.handleChangeLocation(option)}
          disabled={disabled}
          enabled={enabled}
        />
      );
    case 'department':
      return (
        <AgaveDepartmentsDropdown
          disableHeader
          selectedDepartment={transaction.agaveDepartment}
          selectedProject={transaction.agaveProject}
          onValueChange={(option) => handlers.handleChangeDepartment(option)}
          disabled={disabled}
          enabled={enabled}
        />
      );
    case 'employee':
      return (
        <AgaveEmployeesDropdown
          disableHeader
          selectedEmployee={transaction.agaveEmployee}
          locationId={transaction.agaveLocation?.id}
          onValueChange={(option) => handlers.handleChangeEmployee(option)}
          disabled={disabled}
          enabled={enabled}
        />
      );
    case 'subaccount':
      return (
        <AgaveSubaccountsDropdown
          disableHeader
          selectedSubaccount={transaction.agaveSubaccount}
          onValueChange={(option) => handlers.handleChangeSubaccount(option)}
          disabled={disabled}
          enabled={enabled}
        />
      );
    case 'billableItem':
      // SEE-1135 — options come from the per-paidol cached listErpItems (deduped
      // in erp-connection.service); the selected value is read off the
      // transaction's denormalized erpBillableItemId/Name.
      return (
        <ErpBillableItemDropdown
          selectedItemId={transaction.erpBillableItemId}
          selectedItemName={transaction.erpBillableItemName}
          onValueChange={(option) => handlers.handleChangeBillableItem(option)}
          // SEE-1390: per-transaction billable flip (item stays coded).
          billable={transaction.erpBillable}
          onBillableChange={
            supportsBillableFlip ? (billable) => handlers.handleChangeBillable(billable) : undefined
          }
          disabled={disabled}
          enabled={enabled}
        />
      );
    case 'accountingDate':
      if (readOnly) {
        return (
          <Typography variant="body2">
            {transaction.accountingDate ? formatDateOnly(transaction.accountingDate) : '—'}
          </Typography>
        );
      }
      return (
        <AccountingDate
          transaction={transaction as HighnoteTransactionEvent}
          enableHeader={false}
          disabled={disabled}
          onDateChange={handlers.handleChangeAccountingDate}
        />
      );
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Table cell: read-only path (zero hooks)
// ---------------------------------------------------------------------------

function ReadOnlyAgaveDropdown({ row, column }: CellContext<CodingEntityRow, unknown>) {
  const type = (column.columnDef.meta as { dropdownType: CodingColumnId }).dropdownType;
  return renderAgaveDropdown(type, row.original, true, noopHandlers, true);
}

// ---------------------------------------------------------------------------
// Table cell: interactive path (hooks called once, delegates to renderAgaveDropdown)
// ---------------------------------------------------------------------------

// SEE-944 — reads the paidol-level setting that gates the project-by-employee
// location filter. Returns false unless the setting is on AND the parent
// Locations & Departments sync feature is enabled with location sync on.
function useFilterProjectsByEmployeeLocation(): boolean {
  const { selectedPaidol } = useAppSelector(selectUserCompanies);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setting = (selectedPaidol as any)?.erpDepartmentLocationSetting;
  return Boolean(
    setting?.isEnabled && setting?.locationEnabled && setting?.filterProjectsByEmployeeLocationEnabled
  );
}

function useIsTransactionPosting(transaction: HighnoteTransactionEvent): boolean {
  const { postingTransactionIds } = useAppSelector(selectTransactionsSlice);

  return useMemo(() => {
    if (postingTransactionIds.includes(transaction.id)) return true;
    const postingStatus = transaction.erpPostingStatus;
    return postingStatus === ERPPostingStatus.PENDING || postingStatus === ERPPostingStatus.PROCESSING;
  }, [transaction.id, transaction.erpPostingStatus, postingTransactionIds]);
}

function InteractiveAgaveDropdown({ row, column }: CellContext<HighnoteTransactionEvent, unknown>) {
  const transaction = row.original;
  const freshTransaction =
    useAppSelector((state) => selectTransactionById(state, transaction.id)) || transaction;
  const canEdit = useCanEditTransaction('connected');
  const filterProjectsByEmployeeLocation = useFilterProjectsByEmployeeLocation();
  const spectrumVendorLock = useSpectrumPostingVendorLock();
  const {
    handleChangeGlCodes,
    handleChangeProjects,
    handleChangeSubJob,
    handleChangeCostCode,
    handleChangeCostType,
    handleChangeEquipment,
    handleChangeEmCostCode,
    handleChangeEmCostType,
    handleChangeVendor,
    handleChangeLocation,
    handleChangeDepartment,
    handleChangeSubaccount,
    handleChangeEmployee,
    handleChangeBillableItem,
    handleChangeBillable,
    handleChangeAccountingDate,
  } = useAgaveClientUtils({ transaction });

  const disabled = !canEdit(freshTransaction);
  const type = (column.columnDef.meta as { dropdownType: CodingColumnId }).dropdownType;

  return renderAgaveDropdown(
    type,
    freshTransaction,
    disabled,
    {
      handleChangeGlCodes,
      handleChangeProjects,
      handleChangeSubJob,
      handleChangeCostCode,
      handleChangeCostType,
      handleChangeEquipment,
      handleChangeEmCostCode,
      handleChangeEmCostType,
      handleChangeVendor,
      handleChangeLocation,
      handleChangeDepartment,
      handleChangeSubaccount,
      handleChangeEmployee,
      handleChangeBillableItem,
      handleChangeBillable,
      handleChangeAccountingDate,
    },
    false,
    filterProjectsByEmployeeLocation,
    spectrumVendorLock,
    true,
    // A table cell: its project-scoped lists load when the dropdown opens.
    true
  );
}

// ---------------------------------------------------------------------------
// Unified table cell: renders read-only when the row cannot be edited, which is
// also what skips the dropdowns' data fetching.
// ---------------------------------------------------------------------------

function AgaveDropdownCell(ctx: CellContext<HighnoteTransactionEvent | CodingEntityRow, unknown>) {
  const canEdit = useCanEditTransaction('connected');
  const meta = ctx.column.columnDef.meta as {
    dropdownType?: CodingColumnId;
    readOnly?: boolean;
    greyByType?: boolean;
  };
  // SEE-974: the per-transaction "Transaction type" pick (drives greying).
  const codingTypeOverride = useAppSelector((state) =>
    selectCodingTypeOverride(state, (ctx.row.original as HighnoteTransactionEvent).id)
  );

  // SEE-974: grey the coding columns that don't apply to the row's transaction
  // type (Equipment greys the project columns, Project greys the equipment
  // columns, GL greys nothing). Driven by the dropdown pick, else by what's
  // coded. Only active when the type column is shown.
  if (meta?.greyByType) {
    const rowType = greyingCodingType(ctx.row.original as HighnoteTransactionEvent, codingTypeOverride);
    if (rowType && (GREYED_COLUMNS_BY_TYPE[rowType] ?? []).includes(meta.dropdownType ?? '')) {
      // Render exactly like the posted/non-editable cell (disabled dropdown), not
      // a washed-out opacity overlay — type-mismatched columns should read as the
      // same "not editable" state as a posted transaction.
      return <ReadOnlyAgaveDropdown {...(ctx as CellContext<CodingEntityRow, unknown>)} />;
    }
  }

  if (meta?.readOnly || !canEdit(ctx.row.original as HighnoteTransactionEvent)) {
    return <ReadOnlyAgaveDropdown {...(ctx as CellContext<CodingEntityRow, unknown>)} />;
  }
  return <InteractiveAgaveDropdown {...(ctx as CellContext<HighnoteTransactionEvent, unknown>)} />;
}

// ---------------------------------------------------------------------------
// Status cell (separate concern, keeps its own hooks)
// ---------------------------------------------------------------------------

export function AgaveStatusCell({ row }: CellContext<HighnoteTransactionEvent, unknown>) {
  const transaction = row.original;
  const freshTransaction =
    useAppSelector((state) => selectTransactionById(state, transaction.id)) || transaction;
  const isTransactionPosting = useIsTransactionPosting(transaction);
  const { isAgaveTransactionAllowedToCode: isAllowedToCode } = useAgaveClientUtils({ transaction });
  const { isCardholder } = useUserRole();

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {shouldShowReturnedStatus(freshTransaction) ? (
        <ReturnedStatusChip returnedStatus={freshTransaction.returnedStatus} isCardholder={isCardholder} />
      ) : (
        <AgaveStatusChip
          erpApprovalStatus={freshTransaction.erpApprovalStatus}
          agaveExpenseId={freshTransaction.agaveExpenseId}
          erpBypassStatus={freshTransaction.erpBypassStatus}
          isAllowedToCode={isAllowedToCode}
          transaction={freshTransaction}
          isTransactionPosting={isTransactionPosting}
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Column map and module export
// ---------------------------------------------------------------------------

interface AgaveColumnConfig {
  accessorKey: string;
  dropdownType: CodingColumnId;
  size: number;
}

export const AGAVE_COLUMN_MAP: Record<string, AgaveColumnConfig> = {
  glCode: { accessorKey: 'agaveGlCode', dropdownType: 'glCode', size: 190 },
  project: { accessorKey: 'agaveProject', dropdownType: 'project', size: 190 },
  subJob: { accessorKey: 'agaveSubJob', dropdownType: 'subJob', size: 190 },
  costCode: { accessorKey: 'agaveCostCode', dropdownType: 'costCode', size: 190 },
  costType: { accessorKey: 'agaveCostType', dropdownType: 'costType', size: 150 },
  // SEE-974 — Vista equipment coding columns. Flow through the standard column
  // pipeline like every other coding field (gated in useCodingSystemConfig).
  equipment: { accessorKey: 'agaveEquipment', dropdownType: 'equipment', size: 190 },
  emCostCode: { accessorKey: 'agaveEmCostCode', dropdownType: 'emCostCode', size: 190 },
  emCostType: { accessorKey: 'agaveEmCostType', dropdownType: 'emCostType', size: 190 },
  vendor: { accessorKey: 'agaveVendor', dropdownType: 'vendor', size: 190 },
  location: { accessorKey: 'agaveLocation', dropdownType: 'location', size: 190 },
  department: { accessorKey: 'agaveDepartment', dropdownType: 'department', size: 190 },
  employee: { accessorKey: 'agaveEmployee', dropdownType: 'employee', size: 190 },
  accountingDate: { accessorKey: 'accountingDate', dropdownType: 'accountingDate', size: 180 },
  subaccount: { accessorKey: 'agaveSubaccount', dropdownType: 'subaccount', size: 190 },
  // SEE-1135 — per-transaction billable item (Sage Intacct, opt-in gated). The
  // accessorKey is the flat id on the row (not an entity object).
  billableItem: { accessorKey: 'erpBillableItemId', dropdownType: 'billableItem', size: 190 },
};

// SEE-974: the "Transaction type" column (GL / Project / Equipment). For now
// the value is DERIVED from the row's coding (mirrors the sidesheet selector) —
// no codingType field yet, so selecting is a no-op until persistence + column
// greying land as the follow-on.
const TRANSACTION_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'gl', label: 'GL' },
  { value: 'project', label: 'Project' },
  { value: 'equipment', label: 'Equipment' },
];

// SEE-974: which coding columns are greyed (not applicable) for each row type.
// Equipment greys the Project group; Project greys the Equipment group; GL greys
// nothing (it's permissive — you can code anything). Derived from
// CODING_COLUMNS_BY_TYPE so adding a column to a group can't silently leave it
// editable on the opposite type. Applied per-cell when greyByType is set.
const GREYED_COLUMNS_BY_TYPE: Record<string, readonly string[]> = {
  equipment: CODING_COLUMNS_BY_TYPE.project,
  project: CODING_COLUMNS_BY_TYPE.equipment,
  gl: [],
};

// SEE-974: the type that drives column greying (and the Type cell's value) for a
// row, and the value shown in the Type cell. Actual equipment/project coding is
// authoritative and checked FIRST, so a stale dropdown pick can never grey out
// columns that genuinely hold coding. Otherwise the user's explicit pick wins —
// including GL, so selecting GL on an uncoded row sticks (GL greys nothing, since
// GREYED_COLUMNS_BY_TYPE.gl is empty). Falls back to deriving from the coding.
function greyingCodingType(txn: HighnoteTransactionEvent, override?: string): string | null {
  // Shared with the sidesheet selector via resolveDisplayCodingType so the
  // displayed type and the greyed columns can't drift apart (SEE-974).
  return resolveDisplayCodingType(txn, override);
}

// SEE-974: walk up from a node to the nearest ancestor that actually scrolls
// horizontally (overflow-x auto/scroll AND content wider than the box). Returns
// null when nothing overflows — i.e. the table already fits, so there's nothing
// to scroll. Done by measurement rather than a class name so it's robust.
function findHorizontalScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const overflowX = getComputedStyle(node).overflowX;
    if ((overflowX === 'auto' || overflowX === 'scroll') && node.scrollWidth > node.clientWidth + 1) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

function TransactionTypeCell(ctx: CellContext<HighnoteTransactionEvent, unknown>) {
  const { row } = ctx;
  // SEE-1091: honor the column's readOnly meta so surfaces that render coding as
  // locked (e.g. the reimbursements table, useCodingSystemConfig({readOnly:true}))
  // also lock the type picker — otherwise the type stays editable while the codes
  // beside it are display-only.
  const readOnly = Boolean((ctx.column.columnDef.meta as { readOnly?: boolean } | undefined)?.readOnly);
  const cellRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const override = useAppSelector((state) => selectCodingTypeOverride(state, row.original.id));
  // Same gate the coding cells use, so the type (and the coding a type change
  // would clear) can't be mutated by anyone who cannot edit the row.
  const canEdit = useCanEditTransaction('connected');
  const disabled = readOnly || !canEdit(row.original);
  // The displayed type is the user's explicit pick only (resolveDisplayCodingType
  // no longer derives from coding) — coding a value never changes it.
  const type = greyingCodingType(row.original, override);
  // Picking a type drives the column greying (Equipment greys project, Project
  // greys equipment, GL stays neutral until you code one — see greyingCodingType)
  // and, when toggling between Equipment and Project, clears the other group's
  // coding so a row never carries both.
  const handleChange = (event: SelectChangeEvent) => {
    if (disabled) return;
    const picked = event.target.value;
    dispatch(setCodingTypeForTransaction({ id: row.original.id, codingType: picked }));
    // SEE-974: jump the table to the picked type's coding columns so the user
    // lands where they need to code. Scrolls the table's horizontal container so
    // the first column of that group is near the left (leaving a small lead margin).
    const firstColId = CODING_COLUMNS_BY_TYPE[picked as CodingType]?.[0];
    if (!firstColId) return;
    // Defer so the menu close / any layout settles before measuring.
    requestAnimationFrame(() => {
      const container = findHorizontalScrollParent(cellRef.current);
      const header = container?.querySelector<HTMLElement>(`[data-column-id="${firstColId}"]`);
      if (container && header) {
        const delta = header.getBoundingClientRect().left - container.getBoundingClientRect().left;
        container.scrollBy({ left: delta - 16, behavior: 'smooth' });
      }
    });
  };

  return (
    // ref lets handleChange locate the table's horizontal scroll container; the
    // onClick stop keeps dropdown interaction from triggering the row click (which
    // opens the transaction details sidesheet) — same guard CodingEntityDropdown
    // applies to the other coding cells.
    <Box ref={cellRef} onClick={(e) => e.stopPropagation()} sx={{ width: '100%' }}>
      {/* White-box Select matching the action-bar Transaction type filter. */}
      <Select
        size="small"
        displayEmpty
        disabled={disabled}
        value={type ?? ''}
        onChange={handleChange}
        data-dropdown-type="agave-transaction-type"
        renderValue={(selected) => {
          const opt = TRANSACTION_TYPE_OPTIONS.find((o) => o.value === selected);
          return opt ? (
            opt.label
          ) : (
            <Typography component="span" sx={{ color: 'text.secondary', fontSize: 'inherit' }}>
              Type
            </Typography>
          );
        }}
        sx={{ width: '100%', height: 34, backgroundColor: 'white', '& .MuiSelect-select': { py: 0 } }}
      >
        {TRANSACTION_TYPE_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            // Clicking the already-selected type toggles it off (back to "Type").
            // MUI's onChange doesn't fire when the value is unchanged, so the clear
            // is handled here; a different value falls through to onChange.
            onClick={() => {
              if (!disabled && option.value === type) {
                dispatch(setCodingTypeForTransaction({ id: row.original.id, codingType: '' }));
              }
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

function getAgaveDropdownColumns(props: ModuleColumnProps): ColumnDef<HighnoteTransactionEvent, unknown>[] {
  const { isVisible, getLabel, orderedColumnIds, readOnly, isEquipmentCodingEnabled } = props;

  // transactionType + equipment columns are registered in COLUMN_DEFINITIONS and
  // gated into orderedColumnIds by useCodingSystemConfig, so they flow through
  // the same pipeline as every other coding column — no special injection.
  const codingColumns: ColumnDef<HighnoteTransactionEvent, unknown>[] = orderedColumnIds
    .filter((colId) => isVisible(colId))
    .map((colId): ColumnDef<HighnoteTransactionEvent, unknown> | null => {
      // The Transaction type column is a derived selector, not an AGAVE_COLUMN_MAP
      // coding entity — build it inline.
      if (colId === 'transactionType') {
        return {
          id: 'transactionType',
          header: getLabel('transactionType'),
          cell: TransactionTypeCell as (
            ctx: CellContext<HighnoteTransactionEvent, unknown>
          ) => React.ReactElement | null,
          size: 170,
          enableSorting: false,
          enableHiding: false,
          meta: { readOnly },
        } as ColumnDef<HighnoteTransactionEvent, unknown>;
      }
      if (!(colId in AGAVE_COLUMN_MAP)) return null;
      const config = AGAVE_COLUMN_MAP[colId];
      return {
        id: colId,
        accessorKey: config.accessorKey,
        header: getLabel(colId as CodingColumnId),
        cell: AgaveDropdownCell as (
          ctx: CellContext<HighnoteTransactionEvent, unknown>
        ) => React.ReactElement | null,
        size: config.size,
        enableSorting: false,
        enableHiding: true,
        meta: { dropdownType: config.dropdownType, readOnly, greyByType: !!isEquipmentCodingEnabled },
      };
    })
    .filter((col): col is ColumnDef<HighnoteTransactionEvent, unknown> => col !== null);

  return codingColumns;
}

function getAgaveToggleableColumns(): ToggleableColumn[] {
  return [
    { id: 'glCode', label: 'GL Code', defaultVisible: true },
    { id: 'project', label: 'Project', defaultVisible: true },
    { id: 'subJob', label: 'Sub Job', defaultVisible: true },
    { id: 'costCode', label: 'Cost Code', defaultVisible: true },
    { id: 'costType', label: 'Cost Type', defaultVisible: true },
    { id: 'vendor', label: 'Vendor', defaultVisible: true },
    { id: 'location', label: 'Location', defaultVisible: true },
    { id: 'department', label: 'Department', defaultVisible: true },
    { id: 'employee', label: 'Employee', defaultVisible: true },
    { id: 'subaccount', label: 'Subaccount', defaultVisible: true },
    { id: 'billableItem', label: 'Item', defaultVisible: true },
    { id: 'accountingDate', label: 'Accounting Date', defaultVisible: true },
  ];
}

export const agaveDropdownModule: DropdownColumnModule<HighnoteTransactionEvent> = {
  moduleId: 'agave',
  getColumns: getAgaveDropdownColumns,
  getToggleableColumns: getAgaveToggleableColumns,
  StatusCell: AgaveStatusCell,
};

// ---------------------------------------------------------------------------
// Sidesheet display configuration -- pure controlled component
// ---------------------------------------------------------------------------

export interface AgaveSidesheetFormProps {
  /** Current coding values (from a transaction, reimbursement, or local form state) */
  values: CodingEntityRow | undefined;
  /** Called when the user selects a value in any dropdown. Ignored if handlers is provided. */
  onChange?: (field: keyof CodingEntityRow, value: unknown) => void;
  /** Override handlers directly (e.g. for bulk coding with cascade logic). */
  handlers?: AgaveSidesheetHandlers;
  disabled?: boolean;
  visibleColumnIds: CodingColumnId[];
  getLabel: (colId: CodingColumnId) => string;
  // SEE-835 — opt-in (transaction surfaces only, not reimbursements): pre-fill +
  // lock the vendor dropdown to the Connections posting vendor for Spectrum direct.
  spectrumVendorLock?: SpectrumPostingVendorLock;
  // SEE-1390 — false on surfaces whose save path doesn't persist erpBillable
  // (reimbursement sheets); hides the Billable checkbox there.
  supportsBillableFlip?: boolean;
}

function agaveOnChangeToHandlers(
  onChange: (field: keyof CodingEntityRow, value: unknown) => void
): AgaveSidesheetHandlers {
  return {
    handleChangeGlCodes: (v) => onChange('agaveGlCode', v),
    handleChangeProjects: (v) => onChange('agaveProject', v),
    handleChangeSubJob: (v) => onChange('agaveSubJob', v),
    handleChangeCostCode: (v) => onChange('agaveCostCode', v),
    handleChangeCostType: (v) => onChange('agaveCostType', v),
    handleChangeEquipment: (v) => onChange('agaveEquipment', v),
    handleChangeEmCostCode: (v) => onChange('agaveEmCostCode', v),
    handleChangeEmCostType: (v) => onChange('agaveEmCostType', v),
    handleChangeVendor: (v) => onChange('agaveVendor', v),
    handleChangeLocation: (v) => onChange('agaveLocation', v),
    handleChangeDepartment: (v) => onChange('agaveDepartment', v),
    handleChangeSubaccount: (v) => onChange('agaveSubaccount', v),
    handleChangeEmployee: (v) => onChange('agaveEmployee', v),
    // SEE-1135 — pass the whole ErpPostingAccount (or null) in one call under
    // erpBillableItemId; the consumer decides whether to route it to a real
    // handler (TransactionSidesheet) or split it into the id + denormalized name
    // on its local coding state (reimbursement sheet).
    handleChangeBillableItem: (v) => onChange('erpBillableItemId', v),
    // SEE-1390 — the per-transaction billable flip, routed like the item pick.
    handleChangeBillable: (v) => onChange('erpBillable', v),
    handleChangeAccountingDate: (v) => onChange('accountingDate', v),
  };
}

export function AgaveSidesheetForm({
  values,
  onChange,
  handlers: handlersProp,
  disabled = false,
  visibleColumnIds,
  getLabel,
  spectrumVendorLock,
  supportsBillableFlip = true,
}: AgaveSidesheetFormProps) {
  const filterProjectsByEmployeeLocation = useFilterProjectsByEmployeeLocation();

  if (!values) return null;

  const handlers = handlersProp ?? agaveOnChangeToHandlers(onChange!);

  return (
    <CodingSidesheetGrid
      visibleColumnIds={visibleColumnIds}
      getLabel={getLabel}
      renderContent={(colId) => {
        if (!(colId in AGAVE_COLUMN_MAP)) return null;
        return renderAgaveDropdown(
          colId,
          values,
          disabled,
          handlers,
          false,
          filterProjectsByEmployeeLocation,
          spectrumVendorLock,
          supportsBillableFlip
        );
      }}
    />
  );
}

export default agaveDropdownModule;
